/**
 * CartDrawer Component
 * 
 * Right-side sliding panel that shows cart contents.
 * Opens automatically when items are added to cart.
 * 
 * Features:
 * - Smooth slide-in animation from right
 * - 40% width on desktop, full width on mobile
 * - Quantity controls (diamonds: max based on stock, others: multiple)
 * - Navigate to cart for quotation flow
 * - Touch-friendly controls with swipe-to-close
 */

import React, { useEffect, useCallback, useRef, memo, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';

// Constants
const PLACEHOLDER_IMAGE = '/placeholder-ring.jpg';
const MAX_QUANTITY_DEFAULT = 99;
const DEBOUNCE_MS = 300;
const SWIPE_THRESHOLD = 100;

// CSS custom properties for theming
const THEME = {
  primary: 'var(--color-primary, #0C1322)',
  primaryHover: 'var(--color-primary-hover, #1a2436)',
};

// Helper: Check if item is a diamond
const isDiamond = (item) => {
  if (!item) return false;
  return item.item_type === 2 || item.type === 'diamond' || Boolean(item.carat) || Boolean(item.lab);
};

// Helper: Check if item is portfolio/AI (needs quotation)
const isQuotationItem = (item) => {
  if (!item) return false;
  return item.item_type === 3 || item.item_type === 4 || item.type === 'portfolio';
};

// Helper: Check if price is valid
const hasValidPrice = (item) => {
  if (!item) return false;
  const price = item.price;
  if (price === null || price === undefined) return false;
  if (price === '-' || price === '' || price === 'N/A') return false;
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(numPrice) || numPrice <= 0) return false;
  return true;
};

// Helper: Get numeric price safely
const getNumericPrice = (item) => {
  if (!hasValidPrice(item)) return 0;
  const price = item.price || item.total_price || 0;
  return typeof price === 'string' ? parseFloat(price) || 0 : price;
};

// Helper: Get max quantity for item
const getMaxQuantity = (item) => {
  if (!item) return 1;
  // Diamonds can only have qty = available stock (usually 1)
  if (isDiamond(item)) {
    return item.qty || item.available_quantity || item.quantity_available || 1;
  }
  // Other items can have multiple
  return MAX_QUANTITY_DEFAULT;
};

// Debounce hook for quantity updates
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  const debouncedFn = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedFn;
};

// Image error handler (stable reference)
const handleImageError = (e) => {
  e.target.src = PLACEHOLDER_IMAGE;
};

// Memoized cart item component
const CartItem = memo(({ item, onUpdateQuantity, onRemove, formatPrice, updating }) => {
  // Guard against invalid items
  if (!item || !item.key) return null;
  
  const isUpdating = updating === item.key;
  const itemIsDiamond = isDiamond(item);
  const itemNeedsQuote = isQuotationItem(item);
  const priceValid = hasValidPrice(item);
  const maxQty = getMaxQuantity(item);
  
  // Memoized computed values
  const itemImage = useMemo(() => {
    if (item.image) return item.image;
    if (item.images && item.images.length > 0) return item.images[0];
    if (item.thumbnail) return item.thumbnail;
    return PLACEHOLDER_IMAGE;
  }, [item.image, item.images, item.thumbnail]);
  
  const itemName = useMemo(() => {
    if (item.name) return item.name;
    if (item.title) return item.title;
    if (item.product_name) return item.product_name;
    if (item.stock_number) return item.stock_number;
    return 'Custom Jewelry';
  }, [item.name, item.title, item.product_name, item.stock_number]);
  
  const attributes = useMemo(() => {
    const attrs = [];
    
    if (itemIsDiamond) {
      if (item.shape) attrs.push(item.shape);
      if (item.color && item.clarity) attrs.push(`${item.color}-${item.clarity}`);
      else if (item.color) attrs.push(item.color);
      else if (item.clarity) attrs.push(item.clarity);
      if (item.carat) attrs.push(`${item.carat}ct`);
    } else {
      if (item.metal_type) attrs.push(item.metal_type);
      if (item.metal_color) attrs.push(item.metal_color);
      if (item.ring_size) attrs.push(`Size ${item.ring_size}`);
      if (item.stone_quality) attrs.push(item.stone_quality);
    }
    
    return attrs;
  }, [itemIsDiamond, item.shape, item.color, item.clarity, item.carat, item.metal_type, item.metal_color, item.ring_size, item.stone_quality]);
  
  const itemPrice = getNumericPrice(item);
  const quantity = item.quantity || 1;
  
  // Should show quantity controls?
  const showQuantityControls = !itemNeedsQuote;
  const canIncrease = quantity < maxQty;
  const canDecrease = quantity > 1;
  
  // Render price display
  const priceDisplay = useMemo(() => {
    if (itemNeedsQuote) {
      return <span className="text-sm text-amber-600 font-medium">Quote Required</span>;
    }
    if (!priceValid) {
      return <span className="text-sm text-gray-500">Price on Request</span>;
    }
    return (
      <span className="font-semibold text-gray-900 text-sm sm:text-base">
        {formatPrice(itemPrice * quantity)}
      </span>
    );
  }, [itemNeedsQuote, priceValid, formatPrice, itemPrice, quantity]);
  
  return (
    <div className="flex gap-3 py-4 border-b border-gray-100 last:border-0">
      {/* Image */}
      <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden">
        <img 
          src={itemImage} 
          alt={itemName}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={handleImageError}
        />
      </div>
      
      {/* Details */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 text-sm line-clamp-2 leading-tight">
          {itemName}
        </h4>
        
        {attributes.length > 0 && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
            {attributes.join(' • ')}
          </p>
        )}
        
        {/* Item type badges */}
        {itemIsDiamond && (
          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
            Diamond
          </span>
        )}
        {itemNeedsQuote && (
          <span className="inline-block mt-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded">
            Custom Quote
          </span>
        )}
        
        {/* Price & Controls */}
        <div className="flex items-center justify-between mt-2 gap-2">
          {showQuantityControls ? (
            <div className="flex items-center border border-gray-200 rounded-lg">
              {/* Touch-friendly button: min 44px touch target */}
              <button
                onClick={() => onUpdateQuantity(item.key, quantity - 1)}
                disabled={isUpdating || !canDecrease}
                className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
                aria-label="Decrease quantity"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="px-2 sm:px-3 text-sm font-medium min-w-[2rem] text-center">
                {isUpdating ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : quantity}
              </span>
              <button
                onClick={() => onUpdateQuantity(item.key, quantity + 1)}
                disabled={isUpdating || !canIncrease}
                className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors touch-manipulation"
                aria-label="Increase quantity"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <span className="text-xs text-gray-400">Qty: {quantity}</span>
          )}
          
          {priceDisplay}
        </div>
      </div>
      
      {/* Remove Button - Touch-friendly: 44px min */}
      <button
        onClick={() => onRemove(item.key)}
        disabled={isUpdating}
        className="w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors self-start disabled:opacity-50 touch-manipulation"
        aria-label="Remove item"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
});

CartItem.displayName = 'CartItem';

const CartDrawer = () => {
  const navigate = useNavigate();
  const { 
    items, 
    totalItems, 
    isDrawerOpen, 
    closeDrawer, 
    updateQuantity, 
    removeItem,
    loading 
  } = useCart();
  const { formatPrice } = useCurrency();
  const drawerRef = useRef(null);
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState(null);
  
  // Touch/swipe state for mobile
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const [swiping, setSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  
  // Memoized calculations to avoid recalculating on every render
  const { quotationItems, pricedSubtotal } = useMemo(() => {
    const quoteItems = items.filter(isQuotationItem);
    const priced = items.filter(i => hasValidPrice(i) && !isQuotationItem(i));
    const subtotal = priced.reduce((sum, i) => {
      const price = getNumericPrice(i);
      const qty = i.quantity || 1;
      return sum + (price * qty);
    }, 0);
    return { quotationItems: quoteItems, pricedSubtotal: subtotal };
  }, [items]);
  
  // Clear error after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  
  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isDrawerOpen) {
        closeDrawer();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isDrawerOpen, closeDrawer]);
  
  // Lock body scroll when open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);
  
  // Focus trap
  useEffect(() => {
    if (isDrawerOpen && drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isDrawerOpen]);
  
  // Swipe to close handlers for mobile
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    setSwiping(true);
  }, []);
  
  const handleTouchMove = useCallback((e) => {
    if (!swiping) return;
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    // Only allow swiping right (to close)
    if (diff > 0) {
      setSwipeOffset(Math.min(diff, 300));
    }
  }, [swiping]);
  
  const handleTouchEnd = useCallback(() => {
    if (swipeOffset > SWIPE_THRESHOLD) {
      closeDrawer();
    }
    setSwiping(false);
    setSwipeOffset(0);
  }, [swipeOffset, closeDrawer]);
  
  // Handle quantity update with debounce
  const performQuantityUpdate = useCallback(async (key, quantity) => {
    const item = items.find(i => i.key === key);
    if (!item) return;
    
    const maxQty = getMaxQuantity(item);
    const clampedQty = Math.max(1, Math.min(quantity, maxQty));
    
    if (clampedQty < 1) return;
    if (clampedQty === item.quantity) return; // No change needed
    
    setUpdating(key);
    setError(null);
    try {
      await updateQuantity(key, clampedQty);
    } catch (err) {
      console.error('Failed to update quantity:', err);
      setError('Failed to update quantity');
    } finally {
      setUpdating(null);
    }
  }, [updateQuantity, items]);
  
  // Debounced version for rapid clicks
  const handleUpdateQuantity = useDebounce(performQuantityUpdate, DEBOUNCE_MS);
  
  // Handle remove
  const handleRemove = useCallback(async (key) => {
    setUpdating(key);
    setError(null);
    try {
      await removeItem(key);
    } catch (err) {
      console.error('Failed to remove item:', err);
      setError('Failed to remove item');
    } finally {
      setUpdating(null);
    }
  }, [removeItem]);
  
  // Navigate to cart page
  const handleViewCart = useCallback(() => {
    closeDrawer();
    navigate('/cart');
  }, [closeDrawer, navigate]);
  
  // Navigate to products
  const handleStartShopping = useCallback(() => {
    closeDrawer();
    navigate('/products');
  }, [closeDrawer, navigate]);
  
  if (!isDrawerOpen) return null;
  
  const drawerStyle = swiping && swipeOffset > 0 
    ? { transform: `translateX(${swipeOffset}px)`, transition: 'none' } 
    : {};
  
  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300"
        onClick={closeDrawer}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping Cart"
        style={drawerStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="fixed top-0 right-0 h-full w-full sm:w-[90%] md:w-[50%] lg:w-[40%] xl:w-[35%] max-w-[480px] bg-white shadow-2xl z-[9999] flex flex-col transform transition-transform duration-300 ease-out animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-gray-200 safe-area-pt">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            YOUR CART ({totalItems})
          </h2>
          {/* Touch-friendly close button */}
          <button
            onClick={closeDrawer}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="mx-4 mt-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
            {error}
          </div>
        )}
        
        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 overscroll-contain">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <ShoppingBag className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-6 text-sm">Looks like you haven't added anything yet.</p>
              <button
                onClick={handleStartShopping}
                className="px-6 py-3 bg-[#0C1322] text-white rounded-lg hover:bg-[#1a2436] transition-colors font-medium text-sm touch-manipulation"
              >
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="py-2">
              {items.map(item => (
                item?.key ? (
                  <CartItem
                    key={item.key}
                    item={item}
                    onUpdateQuantity={handleUpdateQuantity}
                    onRemove={handleRemove}
                    formatPrice={formatPrice}
                    updating={updating}
                  />
                ) : null
              ))}
            </div>
          )}
        </div>
        
        {/* Footer - Only show if cart has items */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 px-4 sm:px-5 py-4 space-y-3 bg-gray-50 safe-area-pb">
            {/* Summary */}
            {pricedSubtotal > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">Subtotal</span>
                <span className="text-lg font-semibold text-gray-900">{formatPrice(pricedSubtotal)}</span>
              </div>
            )}
            
            {/* Info text */}
            {quotationItems.length > 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                {quotationItems.length} item{quotationItems.length > 1 ? 's' : ''} require{quotationItems.length === 1 ? 's' : ''} a quote. View cart to request pricing.
              </p>
            )}
            
            {/* CTA Button - Touch-friendly */}
            <button
              onClick={handleViewCart}
              className="w-full py-4 bg-[#0C1322] text-white font-semibold rounded-lg hover:bg-[#1a2436] transition-colors flex items-center justify-center gap-2 touch-manipulation active:scale-[0.98]"
            >
              CONTINUE TO CART
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      
      {/* Animation & Safe Area styles */}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out forwards;
        }
        .safe-area-pt {
          padding-top: max(1rem, env(safe-area-inset-top));
        }
        .safe-area-pb {
          padding-bottom: max(1rem, env(safe-area-inset-bottom));
        }
      `}</style>
    </>
  );
};

export default memo(CartDrawer);
